import React, { useEffect, useRef, useState } from 'react'
import useDevice from 'hooks/useDevice'
import Checkbox from './Checkbox'
import { v7 } from 'uuid'
import './style.scss'

interface Option<T> {
  value: T
  name: string
}

interface SelectProps<T = any> {
  name?: string
  disabled?: boolean
  multiple?: boolean
  required?: boolean
  secondaryTheme?: boolean
  className?: string
  warning?: string
  value?: T
  label?: string
  setValue: (value: T) => void
  options: Option<T>[]
}


const Select: React.FC<SelectProps> = ({ name, disabled = false, multiple, required, secondaryTheme = false, className, warning, value, label, setValue, options }) => {
  const device = useDevice()
  const mobile = device.isMobile
  const [expanded, expand] = useState(false)
  const [reversed, setReverse] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const optionsRef = useRef<HTMLDivElement>(null)

  const checkIsReversed = () => {
    const container = containerRef.current
    if (!container) return
    const dimensions = container.getBoundingClientRect()
    setReverse(dimensions.y > (window.innerHeight / 2))
  }

  useEffect(() => {
    checkIsReversed()
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (container && expanded) {
      const unfocus = (event: Event) => {
        if (!container.contains(event.target as Node)) {
          expand(false)
        }
      }

      const root = document.querySelector('#root')
      root?.addEventListener('click', unfocus)
      checkIsReversed()
      return () => {
        root?.removeEventListener('click', unfocus)
      }
    }
  }, [expanded])

  return (
    <div
      ref={containerRef}
      className={`select__container${expanded ? ' --expanded' : ''}${mobile ? ' --mobile' : ''}${secondaryTheme ? ' --secTheme' : ''}${reversed ? ' --reverse' : ''}${className ? ` ${className}` : ''}`}
      style={disabled ? { opacity: 0.5 } : undefined}>
      <div className='input__default'>
        {label && <label className='input__label' htmlFor={name}>
          <span className='label--warning'>{required ? '* ' : ''}</span>
          <span>{label} </span>
          {warning && <span className='label--warning'>
            - {warning}
          </span>}
        </label>}

        <div className='input__main'>
          <div
            id={name}
            className='select'
            onClick={() => {
              if (disabled) return
              expand(e => !e)
            }}>
            <span>
              {(Array.isArray(value) ? value.length > 0 : value !== undefined)
                ? Array.isArray(value) && value.length > 1
                  ? `Wybrano ${value.length} elementów`
                  : (options.find(o => o.value === ((multiple && Array.isArray(value)) ? value[0] : value))?.name || (value as string))
                : options.find(o => o.value === '')?.name || 'Wybierz'}
            </span>
            <i className={`icon icon--chevron-${reversed ? expanded ? 'down' : 'up' : expanded ? 'up' : 'down'}`} />
          </div>
        </div>
      </div>

      <div
        ref={optionsRef}
        className='select__options'>
        {options.map((option, idx) => {
          const isSelected = multiple
            ? Array.isArray(value) && value.includes(option.value)
            : value === option.value
          return <div
            key={idx}
            className='select__option'>
            <Checkbox
              label={option.name}
              name={v7()}
              checked={isSelected}
              changeHandler={() => {
                if (multiple) {
                  const list = Array.isArray(value) ? value : []
                  setValue(isSelected
                    ? list.filter(v => v !== option.value)
                    : [...list, option.value])
                } else {
                  expand(false)
                  setValue(option.value)
                }
              }}
            />
          </div>
        })}
      </div>
    </div>
  )
}

export default Select